/**
 * Per-device key binding preferences for the game modes. Persisted in
 * localStorage (same scope as volume prefs) so a player who customises
 * their keys keeps them across reloads and across modes.
 *
 * Bindings are stored as {@link KeyboardEvent.code} strings (e.g.
 * `"KeyZ"`, `"KeyQ"`) rather than {@link KeyboardEvent.key} so they
 * are layout-independent — a player on AZERTY who rebinds "tap 1" to
 * the physical Z position still gets the right key on QWERTY-labelled
 * UI, and vice versa.
 */
import { MANIA_LANE_COUNT } from '@shared/maniaNote';

/**
 * A binding may be the empty string when the user has cleared a slot
 * but not yet picked a replacement. Empty bindings are ignored by the
 * input sources at lookup time.
 */
export type Keybinding = string;

export type CirclesTapBindings = readonly [Keybinding, Keybinding];
export type ManiaLaneBindings = readonly [Keybinding, Keybinding];
export type PianoLaneBindings = ManiaLaneBindings;
export type GuitarLaneBindings = ManiaLaneBindings;
export type PianoBindings = readonly [
  PianoLaneBindings,
  PianoLaneBindings,
  PianoLaneBindings,
  PianoLaneBindings,
];
export type GuitarBindings = readonly [
  GuitarLaneBindings,
  GuitarLaneBindings,
  GuitarLaneBindings,
  GuitarLaneBindings,
];

export interface KeybindPrefs {
  /**
   * Customisable "tap at cursor" keys for Circles. Space and every
   * piano / guitar lane key always work too — these are the two slots
   * a player sees in the settings UI.
   */
  circlesTap: CirclesTapBindings;
  /**
   * Per-lane piano bindings. Each lane has a primary and an alternate
   * key so a player can hold the home row in either ergonomic position.
   */
  pianoLanes: PianoBindings;
  /**
   * Per-lane Guitar bindings. Defaults to the osu!mania 4K standard
   * (D, F, J, K) for the primary row, plus Q W E R as the alternate
   * — the same QWER row Piano uses as its primary, so muscle memory
   * transfers between modes either way the player chooses to bind.
   * Stored separately from `pianoLanes` so a player can rebind one
   * mode without disturbing the other.
   */
  guitarLanes: GuitarBindings;
}

const STORAGE_KEY = 'gd-keybind-prefs';

export const DEFAULT_KEYBINDS: KeybindPrefs = {
  circlesTap: ['KeyZ', 'KeyX'],
  pianoLanes: [
    ['KeyQ', 'KeyD'],
    ['KeyW', 'KeyF'],
    ['KeyE', 'KeyJ'],
    ['KeyR', 'KeyK'],
  ],
  guitarLanes: [
    ['KeyD', 'KeyQ'],
    ['KeyF', 'KeyW'],
    ['KeyJ', 'KeyE'],
    ['KeyK', 'KeyR'],
  ],
};

/**
 * Codes the game NEVER allows the player to bind to a mode key.
 * Each one would shadow an in-game control if rebound:
 *   - Space — always a Circles tap surface (convention) and one of
 *     the Pulse flip keys.
 *   - Escape — global pause toggle.
 *   - Tab / Enter — focus traversal + button activation in every UI.
 *   - KeyP — global pause toggle; binding it to a lane would pause every tap.
 *
 * NOTE: KeyR is intentionally NOT reserved. The R hotkey only fires
 * while paused or after a run ends, so during
 * active play a lane-bound R behaves like any other key — and KeyR
 * is the historical default for piano lane 3.
 */
const RESERVED_CODES = new Set(['Space', 'Escape', 'Tab', 'Enter', 'KeyP']);

export function isReservedCode(code: string): boolean {
  return RESERVED_CODES.has(code);
}

/**
 * Normalise a single binding from raw JSON. Returns:
 *   - the code itself when it's a non-reserved string (including the
 *     empty string, which means "explicitly cleared" — preserved so the
 *     dedupe logic in `KeybindSettings` survives a save/load round-trip)
 *   - `null` when the value is missing, the wrong type, or a reserved
 *     code (Space/Escape/Tab/Enter/KeyP). The caller falls back to the
 *     default for that slot.
 */
function normaliseBinding(raw: unknown): Keybinding | null {
  if (typeof raw !== 'string') {
    return null;
  }
  if (isReservedCode(raw)) {
    return null;
  }
  return raw;
}

function normaliseCirclesTap(raw: unknown): CirclesTapBindings {
  if (!Array.isArray(raw)) {
    return [...DEFAULT_KEYBINDS.circlesTap];
  }
  const a = normaliseBinding(raw[0]) ?? DEFAULT_KEYBINDS.circlesTap[0];
  const b = normaliseBinding(raw[1]) ?? DEFAULT_KEYBINDS.circlesTap[1];
  return [a, b];
}

function normaliseManiaLanes<T extends PianoBindings | GuitarBindings>(
  raw: unknown,
  defaults: T,
): T {
  if (!Array.isArray(raw)) {
    return cloneManiaDefaults(defaults);
  }
  const out: ManiaLaneBindings[] = [];
  for (let i = 0; i < MANIA_LANE_COUNT; i += 1) {
    const lane = (raw as unknown[])[i];
    const fallback = defaults[i];
    if (!Array.isArray(lane)) {
      out.push([...fallback]);
      continue;
    }
    const primary = normaliseBinding(lane[0]) ?? fallback[0];
    const alternate = normaliseBinding(lane[1]) ?? fallback[1];
    out.push([primary, alternate]);
  }
  return out as unknown as T;
}

function cloneManiaDefaults<T extends PianoBindings | GuitarBindings>(
  defaults: T,
): T {
  return defaults.map((lane) => [...lane] as ManiaLaneBindings) as unknown as T;
}

function normalisePianoLanes(raw: unknown): PianoBindings {
  return normaliseManiaLanes(raw, DEFAULT_KEYBINDS.pianoLanes);
}

function normaliseGuitarLanes(raw: unknown): GuitarBindings {
  return normaliseManiaLanes(raw, DEFAULT_KEYBINDS.guitarLanes);
}

export function loadKeybindPrefs(): KeybindPrefs {
  if (typeof localStorage === 'undefined') {
    return cloneDefaults();
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return cloneDefaults();
    }
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      circlesTap: normaliseCirclesTap(parsed.circlesTap),
      pianoLanes: normalisePianoLanes(parsed.pianoLanes),
      guitarLanes: normaliseGuitarLanes(parsed.guitarLanes),
    };
  } catch (err) {
    console.warn('Failed to load keybind prefs, using defaults:', err);
    return cloneDefaults();
  }
}

export function saveKeybindPrefs(prefs: KeybindPrefs): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  const safe: KeybindPrefs = {
    circlesTap: normaliseCirclesTap(prefs.circlesTap),
    pianoLanes: normalisePianoLanes(prefs.pianoLanes),
    guitarLanes: normaliseGuitarLanes(prefs.guitarLanes),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
  } catch (err) {
    console.warn('Failed to persist keybind prefs:', err);
  }
}

export function resetKeybindPrefs(): KeybindPrefs {
  const defaults = cloneDefaults();
  saveKeybindPrefs(defaults);
  return defaults;
}

function cloneDefaults(): KeybindPrefs {
  return {
    circlesTap: [...DEFAULT_KEYBINDS.circlesTap],
    pianoLanes: cloneManiaDefaults(DEFAULT_KEYBINDS.pianoLanes),
    guitarLanes: cloneManiaDefaults(DEFAULT_KEYBINDS.guitarLanes),
  };
}

/**
 * Apply a single keybinding edit, clearing any *other* slot in the
 * same mode that already holds the same code. Returns a new
 * {@link KeybindPrefs} (input is not mutated).
 *
 * Without this dedupe, rebinding e.g. `KeyF` from Guitar lane 1 to
 * lane 2 would leave both lanes responding to `F` because the input
 * source resolves codes by linear scan — silently producing a
 * double-bind that is only visible via `localStorage` inspection.
 *
 * Cleared slots persist as the empty string `''` (see
 * {@link normaliseBinding}), so the conflict resolution survives a
 * save/load round-trip and shows up in the settings UI as "Unset".
 */
export function applyKeybind(
  prefs: KeybindPrefs,
  edit:
    | { mode: 'circles'; slot: 0 | 1 }
    | { mode: 'piano'; lane: number; slot: 0 | 1 }
    | { mode: 'guitar'; lane: number; slot: 0 | 1 },
  code: Keybinding,
): KeybindPrefs {
  if (edit.mode === 'circles') {
    const next = [...prefs.circlesTap] as [Keybinding, Keybinding];
    for (let i = 0; i < next.length; i += 1) {
      if (i !== edit.slot && next[i] === code && code !== '') {
        next[i] = '';
      }
    }
    next[edit.slot] = code;
    return { ...prefs, circlesTap: next };
  }
  const lanesKey = edit.mode === 'piano' ? 'pianoLanes' : 'guitarLanes';
  const lanes = prefs[lanesKey];
  const nextLanes = lanes.map((laneBindings, laneIdx) => {
    const updated = [...laneBindings] as [Keybinding, Keybinding];
    for (let s = 0; s < updated.length; s += 1) {
      const isTarget = laneIdx === edit.lane && s === edit.slot;
      if (!isTarget && updated[s] === code && code !== '') {
        updated[s] = '';
      }
      if (isTarget) {
        updated[s] = code;
      }
    }
    return updated;
  }) as unknown as PianoBindings & GuitarBindings;
  return { ...prefs, [lanesKey]: nextLanes };
}

/**
 * Resolve a {@link KeyboardEvent.code} to its mania lane index for the
 * given mode (`'piano'` or `'guitar'`), or null if the code isn't
 * bound to any lane in that mode.
 */
export function maniaLaneForCode(
  prefs: KeybindPrefs,
  mode: 'piano' | 'guitar',
  code: string,
): number | null {
  const lanes = mode === 'guitar' ? prefs.guitarLanes : prefs.pianoLanes;
  for (let lane = 0; lane < lanes.length; lane += 1) {
    const [primary, alternate] = lanes[lane];
    if (code === primary || code === alternate) {
      return lane;
    }
  }
  return null;
}

/**
 * Resolve a {@link KeyboardEvent.code} to its piano lane index, or
 * null if the code isn't bound to any piano lane. Thin wrapper over
 * {@link maniaLaneForCode} kept for compatibility with the Piano
 * input source which has used this name since before Guitar landed.
 */
export function pianoLaneForCode(prefs: KeybindPrefs, code: string): number | null {
  return maniaLaneForCode(prefs, 'piano', code);
}

/**
 * Set of every code currently bound to any lane in the given mania
 * mode. Used by the Play page's tap-to-start hotkey listener so
 * pressing a (rebound) lane key kicks off the run, not just the
 * factory-default D F J K / Q W E R. Empty slots are skipped so a
 * cleared binding doesn't accidentally bind "no key" to start.
 */
export function maniaLaneCodes(
  prefs: KeybindPrefs,
  mode: 'piano' | 'guitar',
): ReadonlySet<string> {
  const codes = new Set<string>();
  const lanes = mode === 'guitar' ? prefs.guitarLanes : prefs.pianoLanes;
  for (const lane of lanes) {
    for (const code of lane) {
      if (code) {
        codes.add(code);
      }
    }
  }
  return codes;
}

/**
 * Set of every code that should fire a Circles "tap at cursor". This
 * is the union of:
 *   - the two customisable circles tap keys
 *   - Space (always a tap surface — Circles convention)
 *   - every piano lane key
 *   - every guitar lane key
 *
 * The mania-mode keys are included unconditionally so a player who
 * muscle-memorises Q W E R (Piano) or D F J K (Guitar) can also use
 * them as tap keys in Circles, even with default circles bindings.
 * Empty bindings are skipped so a player who clears a slot doesn't
 * accidentally bind "no key" to a tap.
 */
export function circlesTapCodes(prefs: KeybindPrefs): ReadonlySet<string> {
  const codes = new Set<string>();
  codes.add('Space');
  for (const code of prefs.circlesTap) {
    if (code) {
      codes.add(code);
    }
  }
  for (const lane of prefs.pianoLanes) {
    for (const code of lane) {
      if (code) {
        codes.add(code);
      }
    }
  }
  for (const lane of prefs.guitarLanes) {
    for (const code of lane) {
      if (code) {
        codes.add(code);
      }
    }
  }
  return codes;
}

/**
 * Human-readable label for a {@link KeyboardEvent.code}. The settings
 * UI uses this to render the rebind buttons ("Z", "Numpad 4", "Left
 * Shift") instead of the raw `KeyZ` / `Numpad4` codes that browsers
 * report.
 */
export function describeKeyCode(code: string): string {
  if (!code) {
    return 'Unset';
  }
  if (code.startsWith('Key') && code.length === 4) {
    return code.slice(3);
  }
  if (code.startsWith('Digit') && code.length === 6) {
    return code.slice(5);
  }
  if (code.startsWith('Numpad')) {
    return `Num ${code.slice('Numpad'.length)}`;
  }
  if (code.startsWith('Arrow')) {
    return `${code.slice('Arrow'.length)} arrow`;
  }
  switch (code) {
    case 'Space':
      return 'Space';
    case 'Backquote':
      return '`';
    case 'Minus':
      return '-';
    case 'Equal':
      return '=';
    case 'BracketLeft':
      return '[';
    case 'BracketRight':
      return ']';
    case 'Backslash':
      return '\\';
    case 'Semicolon':
      return ';';
    case 'Quote':
      return "'";
    case 'Comma':
      return ',';
    case 'Period':
      return '.';
    case 'Slash':
      return '/';
    case 'ShiftLeft':
      return 'L Shift';
    case 'ShiftRight':
      return 'R Shift';
    case 'ControlLeft':
      return 'L Ctrl';
    case 'ControlRight':
      return 'R Ctrl';
    case 'AltLeft':
      return 'L Alt';
    case 'AltRight':
      return 'R Alt';
    default:
      return code;
  }
}
